import { Application } from '@features/applications';
import { useDeleteHireflixInterview } from '@features/applications/hooks/useDeleteHireflixInterview';
import { useDeleteSpecificInterview } from '@features/applications/hooks/useDeleteSpecificInterview';
import { Delete } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Dispatch, FC, SetStateAction, useContext, useState } from 'react';
import { toast } from 'react-toastify';

import { DeleteIcon } from '../../vectors/index';

type DeletInterviewModalProps = {
  isShowDeleteInterviewModal: boolean;
  handleClose: () => void;
  setApplicationData?: Dispatch<SetStateAction<Application[] | null>>;
  setApplication?: Dispatch<SetStateAction<Application | null>>;
  application: Application;
  selectedCompanyId?: string | number;
  applicationStatusLabel?: string;
};

const DeletInterviewModal: FC<DeletInterviewModalProps> = ({
  isShowDeleteInterviewModal,
  handleClose,
  setApplicationData,
  setApplication,
  application,
  applicationStatusLabel,
  selectedCompanyId,
}) => {
  const [errorText, setErrorText] = useState('');
  const { deleteInterview, isDeletingInterview } = useDeleteSpecificInterview({
    setErrorText,
  });
  const { deleteHireflixInterview, isDeleteHireflixInterviewLoading } =
    useDeleteHireflixInterview({
      setErrorText,
    });

  const handleDelete = () => {
    const options = {
      onSuccess: (data: {
        status: number;
        objects: Application;
        message: string;
      }) => {
        if (data && data?.status === 200) {
          toast.success(
            data.message || 'HireFlix Interview Deleted successfully!',
          );
          handleClose();
          if (setApplication) {
            setApplication(data.objects);
          }
          if (setApplicationData) {
            setApplicationData((prev) => {
              if (prev) {
                return prev.map((item) => {
                  if (item?.id === data?.objects?.id) {
                    return data?.objects;
                  }
                  return item;
                });
              }
              return prev;
            });
          }
        }
      },
    };

    if (application?.hire_flix_interview) {
      deleteHireflixInterview({ application_id: application?.id }, options);
    } else {
      deleteInterview(
        {
          application_id: application.id,
          ...(application?.calendar_block_invite
            ? { calendar_block_invite: application?.calendar_block_invite?.id }
            : {}),
          ...(application?.calendar_item
            ? { calendar_item: application?.calendar_item?.id }
            : {}),
        },
        options,
      );
    }
  };

  return (
    <Dialog
      open={isShowDeleteInterviewModal}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      disableEnforceFocus
    >
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <br />
        <DeleteIcon />
        <br />
        <Typography variant="h4" sx={{ p: 3 }}>
          Are you sure you want to delete this interview?
        </Typography>
      </DialogTitle>
      <DialogContent>
        {errorText && <Alert severity="error">{errorText}</Alert>}
        <Stack spacing={3}>
          <LoadingButton
            loading={isDeletingInterview || isDeleteHireflixInterviewLoading}
            variant="contained"
            color="error"
            startIcon={<Delete />}
            onClick={handleDelete}
          >
            Delete
          </LoadingButton>
          <Button
            variant="outlined"
            onClick={handleClose}
            startIcon={<CloseIcon />}
          >
            Cancel
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default DeletInterviewModal;
